import type { TextProps } from '@chakra-ui/react';
import { Box, Button, Text } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

export interface ReadMoreTextProps extends Omit<TextProps, 'children'> {
  content: string;
  maxLines?: number;
  expandText?: string;
  seeMoreColor?: string;
  gradientWidth?: number;
  gradientColor?: string;
}

/**
 * A component that truncates text based on number of lines with a gradient overlay and provides a "read more" button
 */
export const ReadMoreText = ({
  content,
  maxLines = 3,
  expandText = 'See more',
  seeMoreColor = 'primary.500',
  gradientWidth = 80,
  gradientColor = 'gray.100',
  ...textProps
}: ReadMoreTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const raf = requestAnimationFrame(() => {
      const lineHeight = parseInt(window.getComputedStyle(el).lineHeight) || 20;
      const lines = Math.ceil(el.scrollHeight / lineHeight);
      setShouldTruncate(lines > maxLines);
    });

    return () => cancelAnimationFrame(raf);
  }, [content, maxLines]);

  return (
    <Box position="relative">
      <Text
        ref={textRef}
        {...textProps}
        style={{
          ...textProps.style,
          WebkitLineClamp: !isExpanded && shouldTruncate ? maxLines : undefined,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {content}
        {shouldTruncate && !isExpanded && (
          <Box
            as="span"
            position="absolute"
            right={0}
            bottom={0}
            width={`${gradientWidth}px`}
            height="1.5em"
            bgGradient="to-r"
            gradientFrom="transparent"
            gradientTo={gradientColor}
            pointerEvents="none"
          />
        )}
      </Text>

      {shouldTruncate && !isExpanded && (
        <Button
          variant="plain"
          color={seeMoreColor}
          onClick={() => setIsExpanded(true)}
          mt={1}
          height="auto"
          fontWeight="medium"
          fontSize="15px"
          position="relative"
          p={0}
          zIndex={1}
        >
          {expandText}
        </Button>
      )}
    </Box>
  );
};
